export type MapTool = 'select' | 'zone' | 'aisle' | 'platform' | 'rack' | 'bulkRack' | 'entryExit' | 'obstacle';

export type AisleSide = 'Left' | 'Right' | 'Top' | 'Bottom';

export interface LocationMapItem {
  id: string;
  type: 'Zone' | 'Aisle' | 'Platform' | 'Rack' | 'Level' | 'Bin';
  code: string;
  name: string;
  gridX: number;
  gridY: number;
  gridWidth: number;
  gridHeight: number;
  parentId: string | null;
  aisleId: string | null;
  aisleSide: AisleSide | null;
  sortOrder: number;
  isPickable: boolean;
  isNew: boolean;
  isModified: boolean;
  isDeleted: boolean;
  color?: string;
}

export interface MapLayerVisibility {
  sortOrder: boolean;
  route: boolean;
  obstacles: boolean;
  labels: boolean;
}

export interface RoutePoint {
  gridX: number;
  gridY: number;
  label?: string;
  locationId?: string;
  /** 'outbound' = entry → last pick (blue), 'return' = last pick → exit (orange) */
  segment?: 'outbound' | 'return';
  /** connector = koridor dışı ikon/raf noktasına ince-kesik bağlantı */
  routeStyle?: 'normal' | 'connector';
  /**
   * Pin (mavi yuvarlak durak no) için görsel koordinat.
   * Boşsa gridX/gridY kullanılır.
   *
   * Neden ayrı? Rota çizgisi, koridorların ortasından akar (engel-aware A*).
   * Bu nedenle bir durağın çizgi noktası "raf önündeki koridor hücresi" olur.
   * Pin'i ise raf'ın gerçek koordinatında göstermek istiyoruz, böylece
   * aynı koridor hücresine projekte edilen birden fazla raf birbirinin pin'ini
   * ezmez ve her seçili raf kendi numarasını taşır.
   */
  pinGridX?: number;
  pinGridY?: number;
}

export function calculateRouteDistance(points: RoutePoint[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.abs(points[i].gridX - points[i - 1].gridX)
           + Math.abs(points[i].gridY - points[i - 1].gridY);
  }
  return total;
}

export function sortOrderToColor(sortOrder: number, maxSortOrder: number): string {
  if (maxSortOrder <= 0) return '#4caf50';
  const ratio = Math.min(1, sortOrder / maxSortOrder);
  const r = Math.round(76 + ratio * (244 - 76));
  const g = Math.round(175 - ratio * (175 - 67));
  const b = Math.round(80 - ratio * (80 - 54));
  return `rgb(${r},${g},${b})`;
}

export interface EntryExitItem {
  id: string;
  nodeType: 'Entry' | 'Exit';
  name: string;
  gridX: number;
  gridY: number;
  isNew: boolean;
  isModified: boolean;
  isDeleted: boolean;
}

// Entry/Exit semantiği:
// gridX/gridY = sol-üst köşe, görsel boyut = 2x2 hücre.
export const ENTRY_EXIT_GRID_SIZE = 2;
export function entryCenterX(item: EntryExitItem): number {
  return item.gridX + ENTRY_EXIT_GRID_SIZE / 2;
}
export function entryCenterY(item: EntryExitItem): number {
  return item.gridY + ENTRY_EXIT_GRID_SIZE / 2;
}

export interface ObstacleItem {
  id: string;
  obstacleType: 'Wall' | 'Column' | 'FireExit' | 'Other';
  gridX: number;
  gridY: number;
  gridWidth: number;
  gridHeight: number;
  isNew: boolean;
  isModified: boolean;
  isDeleted: boolean;
}

export type MapSelectedItem =
  | { kind: 'location'; item: LocationMapItem }
  | { kind: 'entryExit'; item: EntryExitItem }
  | { kind: 'obstacle'; item: ObstacleItem }
  | null;

export const ZONE_COLORS = [
  'rgba(33, 150, 243, 0.18)',
  'rgba(76, 175, 80, 0.18)',
  'rgba(255, 152, 0, 0.18)',
  'rgba(156, 39, 176, 0.18)',
  'rgba(244, 67, 54, 0.18)',
  'rgba(0, 188, 212, 0.18)',
  'rgba(255, 235, 59, 0.22)',
  'rgba(121, 85, 72, 0.18)',
];

export const ZONE_BORDER_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#d32f2f',
  '#0097a7',
  '#f9a825',
  '#5d4037',
];

export const AISLE_COLOR = 'rgba(158, 158, 158, 0.35)';
export const AISLE_BORDER_COLOR = '#757575';

export const PLATFORM_COLOR = 'rgba(0, 150, 136, 0.20)';
export const PLATFORM_BORDER_COLOR = '#00897b';

export const RACK_COLOR = '#5d4037';
export const RACK_BORDER_COLOR = '#3e2723';

export function snapToGrid(value: number): number {
  return Math.round(value);
}

let tempIdCounter = 0;
export function generateTempId(): string {
  return `temp-${Date.now()}-${++tempIdCounter}`;
}

/**
 * Harita kolonu (üst raf) altındaki dikey raf seviyeleri — haritada ayrı kare
 * olarak çizilmez; ağaç görünümünde parent rafın altında listelenir.
 */
export function isShelfLevelRack(
  rack: LocationMapItem,
  locations: LocationMapItem[],
): boolean {
  if (rack.type !== 'Rack' || !rack.parentId) return false;
  const parent = locations.find(l => l.id === rack.parentId && !l.isDeleted && l.type === 'Rack');
  if (!parent) return false;
  return rack.gridX === parent.gridX && rack.gridY === parent.gridY;
}

export function countShelfLevelRacks(columnRackId: string, locations: LocationMapItem[]): number {
  return locations.filter(
    l => l.type === 'Rack' && l.parentId === columnRackId && !l.isDeleted,
  ).length;
}

/** @deprecated isShelfLevelRack kullanın */
export const isVerticallyStackedRack = isShelfLevelRack;

export function countChildRacks(platformId: string, locations: LocationMapItem[]): number {
  return locations.filter(l => l.type === 'Rack' && l.parentId === platformId && !l.isDeleted).length;
}

// ============================================================================
// Faz 1 — Geometri Sıkılaştırma helper'ları
// "1 grid hücresi = 1 metre" konvansiyonu üzerine inşa edilmiştir.
// ============================================================================

export interface BoundsRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Bir öğenin (x,y) konumunu verilen sınırlar içinde tutar.
 * Öğenin tamamı (x..x+w ve y..y+h) bounds dışına çıkmamalıdır.
 * Eğer öğe bounds'dan büyükse, sol-üst köşeye yapıştırılır.
 */
export function clampPositionToBounds(
  x: number,
  y: number,
  w: number,
  h: number,
  bounds: BoundsRect,
): { x: number; y: number } {
  const maxX = bounds.x + Math.max(0, bounds.width - w);
  const maxY = bounds.y + Math.max(0, bounds.height - h);
  return {
    x: Math.min(maxX, Math.max(bounds.x, x)),
    y: Math.min(maxY, Math.max(bounds.y, y)),
  };
}

/**
 * Bir öğenin (w,h) boyutunu verilen sınırlar içinde tutar.
 * (x,y) sabit varsayılır; w,h öyle kısıtlanır ki x+w<=bounds sağı, y+h<=bounds altı.
 */
export function clampSizeToBounds(
  x: number,
  y: number,
  w: number,
  h: number,
  bounds: BoundsRect,
  minW = 1,
  minH = 1,
): { width: number; height: number } {
  const maxW = Math.max(minW, bounds.x + bounds.width - x);
  const maxH = Math.max(minH, bounds.y + bounds.height - y);
  return {
    width: Math.min(maxW, Math.max(minW, w)),
    height: Math.min(maxH, Math.max(minH, h)),
  };
}

/**
 * Bir LocationMapItem'ı BoundsRect olarak döner.
 */
export function rectFromLocation(item: LocationMapItem): BoundsRect {
  return {
    x: item.gridX,
    y: item.gridY,
    width: item.gridWidth,
    height: item.gridHeight,
  };
}

/**
 * Verilen merkez noktasının düştüğü Zone'u bulur (varsa).
 */
export function findContainingZone(
  centerX: number,
  centerY: number,
  locations: LocationMapItem[],
): LocationMapItem | null {
  return locations.find(
    l =>
      l.type === 'Zone'
      && !l.isDeleted
      && centerX >= l.gridX && centerX < l.gridX + l.gridWidth
      && centerY >= l.gridY && centerY < l.gridY + l.gridHeight,
  ) || null;
}

/**
 * Bir öğenin parent Zone'unu (Aisle için ParentId) veya Platform'unu (Rack için ParentId) bulur.
 */
export function findParentLocation(
  item: LocationMapItem,
  locations: LocationMapItem[],
): LocationMapItem | null {
  if (!item.parentId) return null;
  return locations.find(l => l.id === item.parentId && !l.isDeleted) || null;
}

/**
 * Bir Aisle'a yapışık Platform için izin verilen X koordinatını döner (Left/Right için).
 * Left → aisle.gridX - platformWidth (peron koridorun solunda)
 * Right → aisle.gridX + aisle.gridWidth (peron koridorun sağında)
 * Top/Bottom side'larda X serbest olduğu için bu fonksiyon Right davranır (geri uyumluluk).
 */
export function platformXForAisle(
  aisle: LocationMapItem,
  platformWidth: number,
  side: AisleSide | null,
): number {
  if (side === 'Left') return Math.max(0, aisle.gridX - platformWidth);
  return aisle.gridX + aisle.gridWidth;
}

/**
 * Bir Aisle'a yapışık Platform için izin verilen "kilit" alanını (BoundsRect) döner.
 *  - Left/Right: X kilit, Y koridor yüksekliği boyunca serbest.
 *  - Top/Bottom: Y kilit, X koridor genişliği boyunca serbest.
 * Çağıran kod parent zone clamp'ini ayrıca uygulayabilir.
 */
export function platformBoundsForAisle(
  aisle: LocationMapItem,
  platformWidth: number,
  platformHeight: number,
  side: AisleSide | null,
): BoundsRect {
  switch (side) {
    case 'Left':
      return {
        x: aisle.gridX - platformWidth,
        y: aisle.gridY,
        width: platformWidth,
        height: aisle.gridHeight,
      };
    case 'Top':
      return {
        x: aisle.gridX,
        y: aisle.gridY - platformHeight,
        width: aisle.gridWidth,
        height: platformHeight,
      };
    case 'Bottom':
      return {
        x: aisle.gridX,
        y: aisle.gridY + aisle.gridHeight,
        width: aisle.gridWidth,
        height: platformHeight,
      };
    case 'Right':
    default:
      return {
        x: aisle.gridX + aisle.gridWidth,
        y: aisle.gridY,
        width: platformWidth,
        height: aisle.gridHeight,
      };
  }
}

/**
 * Çizilen rect'in koridorun hangi tarafına yapışacağını belirler.
 * Rect merkezi ile koridor merkezi karşılaştırılır; baskın eksene göre side seçilir.
 *  - |dx| >= |dy|  → Left/Right
 *  - aksi halde     → Top/Bottom
 */
export function determinePlatformSide(rect: BoundsRect, aisle: LocationMapItem): AisleSide {
  const rectCx = rect.x + rect.width / 2;
  const rectCy = rect.y + rect.height / 2;
  const aisleCx = aisle.gridX + aisle.gridWidth / 2;
  const aisleCy = aisle.gridY + aisle.gridHeight / 2;
  const dx = rectCx - aisleCx;
  const dy = rectCy - aisleCy;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'Right' : 'Left';
  return dy >= 0 ? 'Bottom' : 'Top';
}

/**
 * Platform rect'ini koridorun ilgili kenarına yapıştırır.
 * Kilit eksen koridor kenarına oturur; serbest eksen kullanıcının çizdiği gibi kalır.
 */
export function snapPlatformToAisleSide(
  rect: BoundsRect,
  aisle: LocationMapItem,
  side: AisleSide,
): BoundsRect {
  switch (side) {
    case 'Left':
      return { x: aisle.gridX - rect.width, y: rect.y, width: rect.width, height: rect.height };
    case 'Right':
      return { x: aisle.gridX + aisle.gridWidth, y: rect.y, width: rect.width, height: rect.height };
    case 'Top':
      return { x: rect.x, y: aisle.gridY - rect.height, width: rect.width, height: rect.height };
    case 'Bottom':
      return { x: rect.x, y: aisle.gridY + aisle.gridHeight, width: rect.width, height: rect.height };
  }
}

/**
 * Verilen merkez noktasına en yakın Aisle'ı döner.
 */
export function findNearestAisle(
  centerX: number,
  centerY: number,
  locations: LocationMapItem[],
): LocationMapItem | null {
  let nearest: LocationMapItem | null = null;
  let minDist = Infinity;
  for (const l of locations) {
    if (l.isDeleted || l.type !== 'Aisle') continue;
    const cx = l.gridX + l.gridWidth / 2;
    const cy = l.gridY + l.gridHeight / 2;
    const dist = Math.abs(centerX - cx) + Math.abs(centerY - cy);
    if (dist < minDist) {
      minDist = dist;
      nearest = l;
    }
  }
  return nearest;
}

/**
 * Bir öğenin verilen Bounds içinde tamamen kapalı olup olmadığını kontrol eder.
 */
export function isFullyInsideBounds(
  x: number,
  y: number,
  w: number,
  h: number,
  bounds: BoundsRect,
): boolean {
  return (
    x >= bounds.x
    && y >= bounds.y
    && x + w <= bounds.x + bounds.width
    && y + h <= bounds.y + bounds.height
  );
}

/**
 * İki dikdörtgenin görsel olarak çakışıp çakışmadığını kontrol eder.
 * Komşuluk (kenar paylaşımı) çakışma sayılmaz.
 */
export function rectsOverlap(a: BoundsRect, b: BoundsRect): boolean {
  return !(
    a.x + a.width <= b.x
    || b.x + b.width <= a.x
    || a.y + a.height <= b.y
    || b.y + b.height <= a.y
  );
}

/**
 * İki öğenin geometrik olarak ÇAKIŞMASI YASAK mı?
 * Konteyner ilişkileri (Zone içinde Aisle/Platform, Platform içinde Rack) çakışma sayılmaz.
 *
 * Yasak çiftler:
 *  - Zone     ↔ Zone
 *  - Aisle    ↔ Platform | Rack   (Aisle ↔ Aisle SERBEST — kesişen koridor sistemi için)
 *  - Platform ↔ Aisle | Platform | Rack (kendi child rack'leri hariç)
 *  - Rack     ↔ Aisle | Rack | Platform (kendi parent platform'u hariç)
 *
 * Not: Aisle ↔ Aisle çakışması serbest bırakıldı çünkü gerçek depolarda
 * yatay × dikey koridor kesişimleri (T- veya +-bağlantı) yaygındır.
 * Backend graf inşası kesişim hücrelerini her iki Aisle'a ait kabul edip
 * bağlantıyı otomatik kurar.
 */
export function isBlockingPair(
  a: LocationMapItem,
  b: LocationMapItem,
): boolean {
  const at = a.type;
  const bt = b.type;
  if (at === 'Zone') return bt === 'Zone';
  if (at === 'Aisle') return bt === 'Platform' || bt === 'Rack';
  if (at === 'Platform') {
    if (bt === 'Rack' && b.parentId === a.id) return false;
    return bt === 'Aisle' || bt === 'Platform' || bt === 'Rack';
  }
  if (at === 'Rack') {
    if (bt === 'Platform' && b.id === a.parentId) return false;
    return bt === 'Aisle' || bt === 'Platform' || bt === 'Rack';
  }
  return false;
}

/**
 * Verilen rect, allLocations içinde "yasak çakışma" yaratan bir öğeye değiyor mu?
 * `excludeIds` cascade veya self-check için hariç tutulur.
 *
 * Faz 4.5 — Koridor-Konteyner istisnası:
 *  - Bir Platform HERHANGİ bir Aisle'ın footprint'i içine TAM giriyorsa (INSIDE mode)
 *    → diğer Aisle'larla olan çakışmalar (Aisle-Aisle overlap kesişimleri dahil)
 *      yasak sayılmaz.
 *  - Bir Aisle'ın footprint'i içine TAM giren Platform varsa
 *    → o Platform ile çakışma sayılmaz.
 *  - Aisle içine TAM giren Platform'un içindeki Rack de Aisle ile çakışma değildir.
 *
 * Not: Aisle-Aisle overlap serbest bırakıldığı için (örn. yatay × dikey kesişim),
 * INSIDE platform parent aisle'ı dışındaki kesişen aisle'larla da rect-overlap
 * yapabilir. Bu kesişim hücreleri her iki aisle'a aittir, dolayısıyla platform
 * yerleşiminde ek bir engel oluşturmazlar.
 */
export function wouldCollide(
  item: LocationMapItem,
  newRect: BoundsRect,
  excludeIds: Set<string>,
  allLocations: LocationMapItem[],
): LocationMapItem | null {
  // Yardımcı: verilen rect, allLocations içindeki HERHANGİ bir aisle'a TAM içeride mi?
  const rectInsideSomeAisle = (rect: BoundsRect, ignoreId?: string): boolean => {
    for (const a of allLocations) {
      if (a.isDeleted || a.type !== 'Aisle') continue;
      if (ignoreId && a.id === ignoreId) continue;
      if (isFullyInsideBounds(rect.x, rect.y, rect.width, rect.height, rectFromLocation(a))) return true;
    }
    return false;
  };

  for (const other of allLocations) {
    if (other.isDeleted || other.id === item.id || excludeIds.has(other.id)) continue;
    if (!isBlockingPair(item, other)) continue;
    if (!rectsOverlap(newRect, rectFromLocation(other))) continue;

    // INSIDE mode istisnaları
    // 1) item Platform, other Aisle: peron HERHANGİ bir aisle'ın içine TAM giriyorsa OK.
    if (item.type === 'Platform' && other.type === 'Aisle') {
      if (rectInsideSomeAisle(newRect)) continue;
    }
    // 2) item Aisle, other Platform: peron HERHANGİ bir aisle'ın içine TAM giriyorsa OK.
    if (item.type === 'Aisle' && other.type === 'Platform') {
      const otherRect: BoundsRect = {
        x: other.gridX, y: other.gridY, width: other.gridWidth, height: other.gridHeight,
      };
      if (rectInsideSomeAisle(otherRect, item.id)) continue;
    }
    // 3) item Rack, other Aisle: rafın parent platformu HERHANGİ bir aisle'a TAM giriyorsa OK.
    if (item.type === 'Rack' && other.type === 'Aisle' && item.parentId) {
      const parent = allLocations.find(l => l.id === item.parentId && !l.isDeleted);
      if (parent && parent.type === 'Platform') {
        const parentRect: BoundsRect = {
          x: parent.gridX, y: parent.gridY, width: parent.gridWidth, height: parent.gridHeight,
        };
        if (rectInsideSomeAisle(parentRect)) continue;
      }
    }
    // 4) item Aisle, other Rack: rafın parent platformu HERHANGİ bir aisle'a TAM giriyorsa OK.
    if (item.type === 'Aisle' && other.type === 'Rack' && other.parentId) {
      const parent = allLocations.find(l => l.id === other.parentId && !l.isDeleted);
      if (parent && parent.type === 'Platform') {
        const parentRect: BoundsRect = {
          x: parent.gridX, y: parent.gridY, width: parent.gridWidth, height: parent.gridHeight,
        };
        if (rectInsideSomeAisle(parentRect, item.id)) continue;
      }
    }

    return other;
  }
  return null;
}

// ============================================================================
// Faz 4.5 — Koridor-Konteyner Modeli (INSIDE peronlar)
// ============================================================================

/**
 * Peron, bir Aisle'ın footprint'i içine TAM giriyorsa "INSIDE" sayılır.
 * Routing ve walkable hesaplarında bu peron hücreleri yürünemez kabul edilir.
 */
export function isPlatformInsideAisle(
  platform: LocationMapItem | BoundsRect,
  aisle: LocationMapItem,
): boolean {
  const rect: BoundsRect = 'gridX' in platform
    ? { x: platform.gridX, y: platform.gridY, width: platform.gridWidth, height: platform.gridHeight }
    : platform;
  return isFullyInsideBounds(rect.x, rect.y, rect.width, rect.height, rectFromLocation(aisle));
}

/**
 * Verilen Aisle'ın yürünebilir hücrelerini döner.
 * = Aisle hücreleri − içine TAM giren peronların hücreleri.
 */
export function getAisleWalkableCells(
  aisle: LocationMapItem,
  allLocations: LocationMapItem[],
): Set<string> {
  const cells = new Set<string>();
  for (let x = aisle.gridX; x < aisle.gridX + aisle.gridWidth; x++) {
    for (let y = aisle.gridY; y < aisle.gridY + aisle.gridHeight; y++) {
      cells.add(`${x},${y}`);
    }
  }
  for (const l of allLocations) {
    if (l.isDeleted || l.type !== 'Platform') continue;
    if (!isPlatformInsideAisle(l, aisle)) continue;
    for (let x = l.gridX; x < l.gridX + l.gridWidth; x++) {
      for (let y = l.gridY; y < l.gridY + l.gridHeight; y++) {
        cells.delete(`${x},${y}`);
      }
    }
  }
  return cells;
}

/**
 * Tüm aisle'lardan tüm peronları çıkarttıktan sonra kalan toplam yürünebilir set.
 */
export function getAllAisleWalkableCells(allLocations: LocationMapItem[]): Set<string> {
  const cells = new Set<string>();
  const aisles = allLocations.filter(l => !l.isDeleted && l.type === 'Aisle');
  for (const a of aisles) {
    const sub = getAisleWalkableCells(a, allLocations);
    sub.forEach(c => cells.add(c));
  }
  return cells;
}

/**
 * INSIDE peron snap'i: çizilen rect'i Aisle'ın bir kenar dilimine kilitler.
 *  - Left  → Aisle'ın sol kolonu (X = aisle.gridX), genişlik kullanıcı çizimi (clamp aisle içi).
 *  - Right → Aisle'ın sağ kolonu (X = aisle.gridX+aisle.gridWidth-w).
 *  - Top   → Aisle'ın üst satırı (Y = aisle.gridY).
 *  - Bottom→ Aisle'ın alt satırı (Y = aisle.gridY+aisle.gridHeight-h).
 * Genişlik/yükseklik koridor sınırları içinde kırpılır.
 * Kullanıcı talebi gereği peronlar koridorun kenarı boyunca TAM boy uzayabilir
 * (zorunlu 1 hücre boşluk bırakma kaldırıldı).
 */
export function platformBoundsInsideAisle(
  drawn: BoundsRect,
  aisle: LocationMapItem,
  side: AisleSide,
): BoundsRect | null {
  const ax = aisle.gridX;
  const ay = aisle.gridY;
  const aw = aisle.gridWidth;
  const ah = aisle.gridHeight;

  if (side === 'Left' || side === 'Right') {
    const maxW = Math.max(0, aw);
    if (maxW < 1) return null;
    const w = Math.min(Math.max(1, drawn.width), maxW);
    const h = Math.min(Math.max(1, drawn.height), ah);
    const y = Math.min(ay + ah - h, Math.max(ay, drawn.y));
    const x = side === 'Left' ? ax : ax + aw - w;
    return { x, y, width: w, height: h };
  }
  // Top | Bottom
  const maxH = Math.max(0, ah);
  if (maxH < 1) return null;
  const h = Math.min(Math.max(1, drawn.height), maxH);
  const w = Math.min(Math.max(1, drawn.width), aw);
  const x = Math.min(ax + aw - w, Math.max(ax, drawn.x));
  const y = side === 'Top' ? ay : ay + ah - h;
  return { x, y, width: w, height: h };
}

/**
 * Verilen merkez noktasını içeren Aisle'ı bulur (varsa).
 * INSIDE-mode tespitinde kullanılır.
 */
export function findContainingAisle(
  pointX: number,
  pointY: number,
  allLocations: LocationMapItem[],
): LocationMapItem | null {
  return allLocations.find(
    l =>
      l.type === 'Aisle'
      && !l.isDeleted
      && pointX >= l.gridX && pointX < l.gridX + l.gridWidth
      && pointY >= l.gridY && pointY < l.gridY + l.gridHeight,
  ) || null;
}

/**
 * A* pathfinding (4-neighbor Manhattan grid).
 * - start/end dahil tüm yol noktalarını döner.
 * - obstacle hücreleri geçilemezdir.
 * - path bulunamazsa [start, end] fallback döner.
 */
export function aStarPath(
  start: RoutePoint,
  end: RoutePoint,
  obstacles: ObstacleItem[],
  bounds: { width: number; height: number },
  walkableCells?: Set<string>,
): RoutePoint[] {
  const sx = Math.round(start.gridX);
  const sy = Math.round(start.gridY);
  const ex = Math.round(end.gridX);
  const ey = Math.round(end.gridY);

  const inBounds = (x: number, y: number) => (
    x >= 0 && y >= 0 && x < bounds.width && y < bounds.height
  );
  const key = (x: number, y: number) => `${x},${y}`;
  const parse = (k: string): { x: number; y: number } => {
    const [x, y] = k.split(',').map(Number);
    return { x, y };
  };
  const heuristic = (x: number, y: number) => Math.abs(x - ex) + Math.abs(y - ey);
  const isWalkable = (x: number, y: number) => {
    if (!inBounds(x, y)) return false;
    if (walkableCells && !walkableCells.has(key(x, y))) return false;
    return true;
  };

  const blocked = new Set<string>();
  for (const ob of obstacles) {
    const x0 = Math.round(ob.gridX);
    const y0 = Math.round(ob.gridY);
    const w = Math.max(1, Math.round(ob.gridWidth));
    const h = Math.max(1, Math.round(ob.gridHeight));
    for (let x = x0; x < x0 + w; x++) {
      for (let y = y0; y < y0 + h; y++) {
      if (inBounds(x, y)) blocked.add(key(x, y));
      }
    }
  }
  // Start/end noktası teknik olarak engel üstündeyse, yolu tamamen kilitlememek için geçilebilir kabul et.
  blocked.delete(key(sx, sy));
  blocked.delete(key(ex, ey));

  if (!isWalkable(sx, sy) || !isWalkable(ex, ey)) {
    return walkableCells ? [start] : [start, end];
  }

  const open = new Set<string>([key(sx, sy)]);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>([[key(sx, sy), 0]]);
  const fScore = new Map<string, number>([[key(sx, sy), heuristic(sx, sy)]]);

  const neighbors = (x: number, y: number): Array<{ x: number; y: number }> => [
    { x: x + 1, y },
    { x: x - 1, y },
    { x, y: y + 1 },
    { x, y: y - 1 },
  ];

  while (open.size > 0) {
    // En düşük fScore'u seç (grid küçük olduğu için linear scan yeterli)
    let currentKey: string | null = null;
    let best = Number.POSITIVE_INFINITY;
    open.forEach((k) => {
      const score = fScore.get(k) ?? Number.POSITIVE_INFINITY;
      if (score < best) {
        best = score;
        currentKey = k;
      }
    });
    if (!currentKey) break;

    if (currentKey === key(ex, ey)) {
      const pathKeys: string[] = [currentKey];
      while (cameFrom.has(pathKeys[pathKeys.length - 1])) {
        pathKeys.push(cameFrom.get(pathKeys[pathKeys.length - 1])!);
      }
      pathKeys.reverse();
      return pathKeys.map((k) => {
        const p = parse(k);
        return { gridX: p.x, gridY: p.y };
      });
    }

    open.delete(currentKey);
    const cur = parse(currentKey);
    for (const nb of neighbors(cur.x, cur.y)) {
      if (!isWalkable(nb.x, nb.y)) continue;
      const nk = key(nb.x, nb.y);
      if (blocked.has(nk)) continue;

      const tentativeG = (gScore.get(currentKey) ?? Number.POSITIVE_INFINITY) + 1;
      if (tentativeG < (gScore.get(nk) ?? Number.POSITIVE_INFINITY)) {
        cameFrom.set(nk, currentKey);
        gScore.set(nk, tentativeG);
        fScore.set(nk, tentativeG + heuristic(nb.x, nb.y));
        open.add(nk);
      }
    }
  }

  return walkableCells ? [start] : [start, end];
}
